import React, { useState, useEffect } from 'react';

const Wheel = ({ entries, triggerSpin, setTriggerSpin, setIsSpinning, isSpinning, onSpinComplete }) => {
  const [currentAngle, setCurrentAngle] = useState(0);
  const radius = 500; // Radius for the wheel
  const center = radius;
  const angle = (2 * Math.PI) / entries.length;

  // Handle the spin action when triggerSpin is true
  useEffect(() => {
    if (triggerSpin) {
      spinWheel();
    }
  }, [triggerSpin]);

  const spinWheel = () => {
    if (isSpinning) return;
    setIsSpinning(true);
    setTriggerSpin(false); // Reset the trigger

    const spins = Math.floor(Math.random() * 5) + 5; // Random spins
    const spinAngle = spins * 360 + Math.random() * 360; // Total spin angle
    const startTime = performance.now();

    const animate = (time) => {
      const elapsed = time - startTime;
      const progress = Math.min(elapsed / 6000, 1); // Slower spin speed
      const easedAngle = easeOutCubic(progress) * spinAngle;

      setCurrentAngle(easedAngle);

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        setIsSpinning(false);
        const finalAngle = (360 - ((easedAngle + 90) % 360)) % 360;
        const segmentAngle = 360 / entries.length;
        const winningIndex = Math.floor(finalAngle / segmentAngle) % entries.length;

        onSpinComplete(entries[winningIndex].name); // Call spin complete
      }
    };

    requestAnimationFrame(animate);
  };

  // Easing function for smooth animation
  function easeOutCubic(t) {
    return (--t) * t * t + 1;
  }

  const getPathData = (startAngle, endAngle) => {
    const x1 = center + radius * Math.cos(startAngle);
    const y1 = center + radius * Math.sin(startAngle);
    const x2 = center + radius * Math.cos(endAngle);
    const y2 = center + radius * Math.sin(endAngle);

    return `M${center},${center} L${x1},${y1} A${radius},${radius} 0 0,1 ${x2},${y2} Z`;
  };

  // To shorten text with ellipsis, we'll use a custom function to fit within bounds
  const getText = (text, maxLength = 8) => {
    if (text.length > maxLength) {
      return text.substring(0, maxLength) + '...';
    }
    return text;
  };

  // Calculate font size based on the number of entries
  const calculateFontSize = () => {
    // Define minimum and maximum font sizes
    const minFontSize = 12;
    const maxFontSize = 30;

    // Calculate font size based on the number of entries
    const fontSize = Math.max(minFontSize, Math.min(maxFontSize, (maxFontSize - minFontSize) * (10 / entries.length) + minFontSize));
    return fontSize;
  };

  // Handle wheel click to trigger spin
  const handleWheelClick = () => {
    if (!isSpinning) {
      setTriggerSpin(true); // Trigger the spin when clicked
    }
  };

  return (
    <div className="wheel-container">
      <svg
        width={radius * 2}
        height={radius * 2}
        viewBox={`0 0 ${radius * 2} ${radius * 2}`}
        style={{ display: 'block', margin: 'auto' }}
        onClick={handleWheelClick}  // Add click event to trigger the spin
      >
        <g transform={`rotate(${currentAngle}, ${center}, ${center})`}>
          {entries.map((entry, index) => {
            const startAngle = angle * index;
            const endAngle = startAngle + angle;

            const textAngle = startAngle + angle / 2;
            const textX = center + (radius / 1.5) * Math.cos(textAngle);
            const textY = center + (radius / 1.5) * Math.sin(textAngle);

            const fontSize = calculateFontSize(); // Dynamic font size

            return (
              <g key={index}>
                <path
                  d={getPathData(startAngle, endAngle)}
                  fill={entry.color} // Use color from the entry object
                />
                <text
                  x={textX}
                  y={textY}
                  transform={`rotate(${(textAngle * 180) / Math.PI}, ${textX}, ${textY})`} 
                  textAnchor="middle"
                  fontSize={fontSize}  // Use the dynamic font size here
                  fontFamily="Poppins, sans-serif"
                  fill="white"
                  fontWeight="600"
                  style={{ 
                    userSelect: 'none', 
                    dominantBaseline: 'middle',  // Ensure vertical centering
                    textOverflow: 'ellipsis',    // Apply ellipsis to overflow text
                    overflow: 'hidden', 
                    whiteSpace: 'nowrap',        // Prevent text wrapping
                    maxWidth: '90%'              // Ensure text fits inside the segment
                  }}
                >
                  {getText(entry.name)}  {/* Ensure ellipsis when text is too long */}
                </text>
              </g>
            ); 
          })}
        </g>
      </svg>

      {/* Separate div for the pointer outside the wheel */}
      <div className="wheel-pointer"></div>
    </div>
  );
};

export default Wheel;